import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { SECURITY_CONFIG } from '../config/app.config';
import { authService } from '../services/auth.service';
import logoImage from '../assets/images/app-logo.png';
import { useLang } from '../contexts/LangContext';

const PIN_LOCKOUT_UNTIL_KEY = 'fishAppPinLockoutUntil';

interface PinLoginProps {
  onSuccess: () => void;
  onForgotPin: () => void;
  onCancel?: () => void;
}

const PinLogin: React.FC<PinLoginProps> = ({ onSuccess, onForgotPin, onCancel }) => {
  const { t } = useLang();
  const [pin, setPin] = useState<string[]>(Array(SECURITY_CONFIG.PIN_LENGTH).fill(''));
  const [error, setError] = useState<string>('');
  const [attempts, setAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [lockoutUntil, setLockoutUntil] = useState<number | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Focus first input on mount
    inputRefs.current[0]?.focus();

    // Restore lockout state (session-level)
    try {
      const raw = sessionStorage.getItem(PIN_LOCKOUT_UNTIL_KEY);
      const until = raw ? Number(raw) : NaN;
      if (Number.isFinite(until) && until > Date.now()) {
        setIsLocked(true);
        setLockoutUntil(until);
        setError(t('pin_pinLocked'));
      }
    } catch {
      // ignore
    }

    // Handle viewport resize (keyboard appearing/disappearing)
    const handleResize = () => {
      if (containerRef.current && document.activeElement?.tagName === 'INPUT') {
        // Scroll the container into view when keyboard is visible
        setTimeout(() => {
          containerRef.current?.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
        }, 100);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [t]);

  useEffect(() => {
    if (!isLocked || !lockoutUntil) return;

    const interval = window.setInterval(() => {
      if (Date.now() >= lockoutUntil) {
        setIsLocked(false);
        setLockoutUntil(null);
        setAttempts(0);
        setError('');
        setPin(Array(SECURITY_CONFIG.PIN_LENGTH).fill(''));
        try {
          sessionStorage.removeItem(PIN_LOCKOUT_UNTIL_KEY);
        } catch {
          // ignore
        }
        inputRefs.current[0]?.focus();
      }
    }, 500);

    return () => window.clearInterval(interval);
  }, [isLocked, lockoutUntil]);

  const handlePinChange = (index: number, value: string) => {
    // Only allow numbers
    if (!/^\d*$/.test(value)) return;

    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);
    setError('');

    // Ensure input stays visible on mobile
    if (containerRef.current) {
      containerRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
    }

    // Move to next input
    if (value && index < SECURITY_CONFIG.PIN_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Check if PIN is complete
    if (newPin.every(digit => digit !== '') && index === SECURITY_CONFIG.PIN_LENGTH - 1) {
      validatePin(newPin.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      // Move to previous input on backspace if current is empty
      inputRefs.current[index - 1]?.focus();
    }
  };

  const validatePin = async (enteredPin: string) => {
    try {
      if (isLocked) return;

      const isValid = await authService.validatePin(enteredPin);
      
      if (isValid) {
        onSuccess();
      } else {
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);
        
        if (newAttempts >= SECURITY_CONFIG.MAX_PIN_ATTEMPTS) {
          const until = Date.now() + SECURITY_CONFIG.LOCKOUT_DURATION * 60 * 1000;
          setIsLocked(true);
          setLockoutUntil(until);
          setError(t('pin_pinLocked'));
          try {
            sessionStorage.setItem(PIN_LOCKOUT_UNTIL_KEY, String(until));
          } catch {
            // ignore
          }
        } else {
          setError(t('pin_incorrectPin'));
          setPin(Array(SECURITY_CONFIG.PIN_LENGTH).fill(''));
          inputRefs.current[0]?.focus();
        }
      }
    } catch (error) {
      setError(t('pin_pinError'));
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, SECURITY_CONFIG.PIN_LENGTH);
    
    if (/^\d+$/.test(pastedData)) {
      const newPin = pastedData.split('');
      setPin([...newPin, ...Array(SECURITY_CONFIG.PIN_LENGTH - newPin.length).fill('')]);
      
      if (newPin.length === SECURITY_CONFIG.PIN_LENGTH) {
        validatePin(pastedData);
      }
    }
  };

  return (
    <div className="min-h-[100dvh] bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center px-4 py-4 overflow-y-auto">
      <motion.div
        ref={containerRef}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md my-auto"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src={logoImage}
            alt={t("appTitle")}
            className="w-16 h-16 mx-auto mb-4"
          />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            {t('pin_enterPin')}
          </h2>
          <p className="text-gray-600 text-sm">
            {t('pin_enterPinToUnlock')}
          </p>
        </div>

        {/* PIN Input */}
        <div className="flex justify-center gap-3 mb-6">
          {pin.map((digit, index) => (
            <input
              key={index}
              ref={(el) => { inputRefs.current[index] = el; }}
              type="tel"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handlePinChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onFocus={() => {
                // Ensure container stays centered when input is focused (mobile keyboard)
                setTimeout(() => {
                  containerRef.current?.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'center' 
                  });
                }, 100);
              }}
              onPaste={index === 0 ? handlePaste : undefined}
              disabled={isLocked}
              className={`
                w-14 h-14 text-center text-2xl font-bold rounded-lg border-2
                ${error ? 'border-red-500' : 'border-gray-300'}
                ${isLocked ? 'bg-gray-100' : 'bg-white'}
                focus:border-blue-500 focus:outline-none
                transition-colors
              `}
            />
          ))}
        </div>

        {/* Error Message */}
        {error && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-red-500 text-sm text-center mb-4"
          >
            {error}
          </motion.p>
        )}

        {/* Attempts Counter */}
        {attempts > 0 && !isLocked && (
          <p className="text-gray-500 text-xs text-center mb-4">
            {t('pin_attemptsRemaining')}: {SECURITY_CONFIG.MAX_PIN_ATTEMPTS - attempts}
          </p>
        )}

        {/* Actions */}
        <div className="space-y-3">
          <button
            onClick={onForgotPin}
            className="w-full text-blue-600 hover:text-blue-700 font-medium text-sm py-2"
          >
            {t('pin_forgotPin')}
          </button>

          {onCancel && (
            <button
              onClick={onCancel}
              className="w-full text-gray-600 hover:text-gray-700 font-medium text-sm py-2"
            >
              {t('pin_cancel')}
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default PinLogin;
